/**
 * Screen 12 — 铸剑阁（V8 Master-Detail 重设计）
 *
 * 设计准绳：design/v8/v8-001-forge-redesign.md
 * 美学方向：Ardot v2.0 战术美学（0 圆角 / 1-2px 描边 / monospace / 锐角几何）
 * 交互模式：五章卡片横排 → 点击进入单列章节终端 → 返回恢复
 *
 * 模块拆分（render/forge/）：
 *   - types.ts      类型 + CHAPTERS mock 数据
 *   - svg-assets.ts SVG 资产（K 徽章保留，淬火/DAG 不再使用）
 *   - modal.ts      浮层系统（关 卡详情/档案/K 等级）
 *   - screen-12-forge.ts (本文件)  入口 + 主渲染 + 事件绑定
 *
 * 路由：#/forge
 */

import { createFromLevel, DEFAULT_BOT_CODE } from "../storage/project-store";
import { pathFor } from "../router/screen-router";
import { renderHeaderHTML, bindHeaderEvents } from "../components/header-bar";
import {
  CHAPTERS,
  FORGE_LEVEL_MAP,
  type ChapterData, type LevelItem,
} from "../render/forge/types";
import { computeChapterStates } from "../services/forge-progress-service";
import { preloadForgeLevel } from "../render/forge/level-preloader";
import { renderLevelLoadingPage } from "./level-loading-page";
import { isLoggedIn } from "../core/user-context";
import { isOnboarding } from "../onboarding/onboarding-state";

const PENDING_AUTH_PATH_KEY = "battlecode-pending-auth-path";
const PENDING_FORGE_KEY = "battlecode-pending-forge";

/** 当前动态章节数据（render 时注入，event handler 读取） */
let activeChapters: ChapterData[] = CHAPTERS;
// ===== 入口 =====

let forgeGo: (path: string) => void;

export function renderScreen12Forge(go: (path: string) => void): void {
  forgeGo = go;
  const app = document.getElementById("app");
  if (!app) return;

  // 每次渲染时动态计算章节状态
  activeChapters = computeChapterStates();

  // V16: 新手引导模式下，确保 ch0 显示为 current 并高亮
  const onboarding = isOnboarding();
  if (onboarding) {
    const ch0 = activeChapters.find((c) => c.num === 0);
    if (ch0) {
      ch0.state = "current";
    }
  }

  app.innerHTML = `
    ${renderHeaderHTML({
      backPath: pathFor("armory-hall"),
      title: "铸 剑 阁",
      subtitle: "",
      statusText: "ONLINE ●  SYS:ARMED",
      topbarClassName: "s12-topbar",
    })}
    <div class="s12-root">
      ${renderMain()}
    </div>
  `;

  bindEvents();
  resumePendingForge();
}

// ===== 主区域 =====

function renderMain(): string {
  return `
    <div class="s12-main" id="s12-main">
      <div class="s12-master-column">
        <button class="s12-master-back" id="s12-master-back" type="button">
          <span aria-hidden="true">←</span> 返回章列表
        </button>
        <div class="s12-chapter-strip" id="s12-chapter-strip">
          ${activeChapters.map((ch, i) => {
            let html = renderChapterCard(ch);
            if (ch.anchor && i < activeChapters.length - 1) {
              html += renderCardAnchor(ch);
            }
            return html;
          }).join("")}
        </div>
      </div>
      <div class="s12-detail-panel" id="s12-detail-panel"></div>
    </div>
  `;
}

// ===== 章卡片 =====

function renderChapterCard(ch: ChapterData): string {
  const stateClass = ch.state === "completed" ? "s12-card--completed"
    : ch.state === "current" ? "s12-card--current"
    : "s12-card--locked";
  const badgeClass = ch.state === "completed" ? "s12-card-badge--completed"
    : ch.state === "current" ? "s12-card-badge--current"
    : "s12-card-badge--locked";
  const badgeText = ch.state === "completed" ? "✓ 已通关"
    : ch.state === "current" ? "▶ 进行中"
    : "🔒 需通关上一章";
  const actionText = ch.state === "completed" ? "查看章节"
    : ch.state === "current" ? "继续学习"
    : "";

  // 章插图映射（Ardot 战术美学统一风格插画）
  const chapterIllustrations: Record<number, string> = {
    1: "/images/forge/ch1-anvil.png",
    2: "/images/forge/ch2-tank-drone.png",
    3: "/images/forge/ch3-pincer.png",
    4: "/images/forge/ch4-formation.png",
    5: "/images/forge/ch5-victory-insignia.png",
  };
  const illusSrc = chapterIllustrations[ch.num] ?? "";
  const cardInteraction = ch.state === "locked"
    ? ""
    : `role="button" tabindex="0" aria-label="${actionText}章${toChineseNum(ch.num)}·${ch.title}"`;

  return `
    <div class="s12-card ${stateClass}" data-ch="${ch.num}" ${cardInteraction}>
      <div class="s12-card-illus ${ch.illusCls}">
        <img class="s12-card-illus-img" src="${illusSrc}" alt="章${toChineseNum(ch.num)}·${ch.title}" loading="lazy">
        <div class="s12-card-illus-num">CH.${ch.num}</div>
      </div>
      <div class="s12-card-body">
        <div class="s12-card-ch-num">CHAPTER ${ch.num}</div>
        <div class="s12-card-title-row">
          <div class="s12-card-ch-title">章${toChineseNum(ch.num)}·${ch.title}</div>
          <div class="s12-card-units">
            ${ch.units.map(u => {
              if (!u.cls) return `<span class="s12-card-unit s12-card-unit--muted">${u.name}</span>`;
              return `<span class="s12-card-unit s12-card-unit--${u.cls}">${u.name}</span>`;
            }).join(" ")}
          </div>
        </div>
        <div class="s12-card-meta-row">
          <div class="s12-card-badge ${badgeClass}">${badgeText}</div>
          <div class="s12-card-ch-sub">${ch.sub}</div>
          <div class="s12-card-score">${ch.progress.done}/${ch.progress.total}</div>
        </div>
        <div class="s12-card-ch-desc">${getChapterDesc(ch.num)}</div>
        ${actionText ? `<button class="s12-card-action" type="button" aria-label="${actionText}章${toChineseNum(ch.num)}·${ch.title}">${actionText} <span aria-hidden="true">→</span></button>` : ""}
      </div>
    </div>
  `;
}

// ===== 卡片间锚点 =====

function renderCardAnchor(ch: ChapterData): string {
  const anchor = ch.anchor!;
  const dotClass = anchor.unlocked ? "s12-anchor-dot--unlocked" : "";
  return `
    <div class="s12-card-anchor" data-ch="${ch.num}" onclick="void(0)">
      <div class="s12-anchor-tip">${anchor.tip.replace(/\n/g, "<br>")}</div>
      <div class="s12-anchor-line"></div>
      <div class="s12-anchor-dot ${dotClass}"></div>
      <div class="s12-anchor-label">${anchor.unlocked ? "档案" : "待解锁"}</div>
    </div>
  `;
}

// ===== 详情面板渲染（动态） =====

function renderDetail(ch: ChapterData): string {
  const illustration = getChapterIllustration(ch.num);
  return `
    <div class="s12-detail-body">
      ${renderChapterDossier(ch, illustration)}
      ${renderCurrentObjective(ch)}
      <div class="s12-detail-heading">
        <h2>训练关卡</h2>
        <span>CHAPTER ${ch.num} // ${ch.sections.length} SECTIONS · ${ch.progress.done}/${ch.progress.total}</span>
      </div>
      ${ch.sections.map(sec => renderSection(sec)).join("")}
      ${renderExam(ch)}
      ${renderStory(ch)}
    </div>
  `;
}

function renderChapterDossier(ch: ChapterData, illustration: string): string {
  const statusText = ch.state === "completed" ? "✓ 已通关" : ch.state === "current" ? "▶ 进行中" : "○ 未解锁";
  return `
    <section class="s12-chapter-dossier" aria-label="章信息">
      <button class="s12-detail-back" id="s12-detail-back" type="button"><span aria-hidden="true">←</span> 返回章列表</button>
      <div class="s12-dossier-frame">
        <div class="s12-dossier-illus">
          <img src="${illustration}" alt="章${toChineseNum(ch.num)}·${ch.title}" loading="lazy">
          <span>CH.${ch.num}</span>
        </div>
        <div class="s12-dossier-copy">
          <div class="s12-dossier-kicker">CHAPTER ${ch.num}</div>
          <div class="s12-dossier-title-row">
            <h1>章${toChineseNum(ch.num)}·${ch.title}</h1>
            <span>${ch.sub}</span>
          </div>
          <p>${getChapterDesc(ch.num)}</p>
          <div class="s12-dossier-units">${ch.units.map(u => `<span class="s12-dossier-unit s12-dossier-unit--${u.cls || "muted"}">${u.name}</span>`).join("")}</div>
        </div>
        <div class="s12-dossier-state">
          <div class="s12-dossier-status"><span>${statusText}</span><strong>${ch.progress.done}/${ch.progress.total}</strong></div>
          <div class="s12-dossier-state-label">当前训练进度<div class="s12-dossier-progress"><span style="width:${ch.progress.total ? Math.round(ch.progress.done / ch.progress.total * 100) : 0}%"></span></div></div>
        </div>
      </div>
    </section>
  `;
}

function renderCurrentObjective(ch: ChapterData): string {
  const current = ch.sections.flatMap(sec => sec.items).find(item => item.state === "current");
  const objective = current
    ? `${current.name}：${current.teach}`
    : ch.state === "completed" ? "本章训练已完成，准备进入晋升试炼。" : "完成上一章后解锁本章训练。";
  const action = current
    ? `<button class="s12-objective-action" type="button" data-lid="${current.id}">继续推演 <span aria-hidden="true">→</span></button>`
    : "";
  return `
    <section class="s12-objective ${current ? "s12-objective--active" : ""}" aria-label="当前训练目标">
      <div class="s12-objective-copy">
        <span class="s12-objective-label">CURRENT OBJECTIVE</span>
        <strong>${current ? `${current.id} · ${current.name}` : ch.state === "completed" ? "章节完成" : "章节未解锁"}</strong>
        <span class="s12-objective-text">${objective}</span>
      </div>
      ${action}
    </section>
  `;
}

function renderSection(sec: import("../render/forge/types").SectionData): string {
  const completed = sec.items.filter(item => item.state === "completed").length;
  // 从第一个 level 的 ID 推导节号，如 "1.1.1" → "节 1.1"
  const sectionLabel = sec.items.length > 0
    ? `节 ${sec.items[0].id.split(".").slice(0, 2).join(".")}`
    : "";
  return `
    <section class="s12-section-card">
      <div class="s12-section-head">
        <span class="s12-section-head-dot"></span>
        <span>${sec.label}</span>
        <span class="s12-section-count">${completed}/${sec.items.length}</span>
      </div>
      ${sec.story ? `<div class="s12-inter-story">${sec.story.text}</div>` : ""}
      <div class="s12-level-tree">
        ${sec.items.map(lv => renderLevelCard(lv, sectionLabel)).join("")}
      </div>
    </section>
  `;
}

function renderLevelCard(lv: LevelItem, sectionLabel?: string): string {
  const stateIcon = lv.state === "completed" ? "✓" : lv.state === "current" ? "▶" : lv.state === "available" ? "◆" : "○";
  const statusLabel = lv.state === "completed" ? "已完成" : lv.state === "current" ? "进行中" : lv.state === "available" ? "可开始" : "待解锁";
  const rankDisplay = lv.state === "completed" && lv.rank && lv.rank !== "-" ? lv.rank : "";
  const locked = lv.state === "locked";
  return `
    <article class="s12-level-card s12-level-card--${lv.state}" data-lid="${lv.id}" ${locked ? "" : 'role="button" tabindex="0"'}>
      <div class="s12-level-card-illus">
        <img src="${getLevelCover(lv.id)}" alt="" loading="lazy">
      </div>
      <div class="s12-level-card-content">
        <div class="s12-level-card-top">
          <span class="s12-level-card-status">${stateIcon}</span>
          <span class="s12-level-card-id">${lv.id}</span>
          ${sectionLabel ? `<span class="s12-level-card-section">${sectionLabel}</span>` : ""}
          <span class="s12-level-card-badge">${statusLabel}</span>
          ${rankDisplay ? `<span class="s12-level-card-rank">${rankDisplay}</span>` : ""}
        </div>
        <div class="s12-level-card-name">${lv.name}</div>
        <div class="s12-level-card-sep"></div>
        <div class="s12-level-card-teach">${lv.teach}</div>
        <div class="s12-level-card-foot">
          <span>${lv.units.join(" · ")}</span>
          ${locked ? "<span>锁定</span>" : `<span>开始推演 <span aria-hidden="true">→</span></span>`}
        </div>
      </div>
    </article>
  `;
}

function renderExam(ch: ChapterData): string {
  if (!ch.exam) return "";
  const exam = ch.exam;
  const locked = exam.state === "locked";
  const stateIcon = exam.state === "completed" ? "✓" : locked ? "○" : "◆";
  return `
    <section class="s12-section-card s12-section-card--special">
      <div class="s12-section-head">
        <span class="s12-section-head-dot s12-section-head-dot--special"></span>
        <span>特殊关卡 · 晋升试炼</span>
      </div>
      <div class="s12-level-tree">
        <article class="s12-level-card s12-level-card--exam ${locked ? "s12-level-card--locked" : "s12-level-card--available"}" data-exam="true" ${locked ? "" : 'role="button" tabindex="0"'}>
          <div class="s12-level-card-illus">
            <img src="${getLevelCover(`exam-${ch.num}`)}" alt="" loading="lazy">
          </div>
          <div class="s12-level-card-content">
            <div class="s12-level-card-top">
              <span class="s12-level-card-status">${stateIcon}</span>
              <span class="s12-level-card-id">SPECIAL</span>
              ${exam.rank ? `<span class="s12-level-card-rank">${exam.rank}</span>` : ""}
            </div>
            <div class="s12-level-card-name">${exam.name}</div>
            <div class="s12-level-card-teach">${exam.desc}</div>
            <div class="s12-level-card-foot">
              <span class="s12-level-card-exam-label">晋升试炼 · ${exam.reward}</span>
              ${locked ? "<span>锁定</span>" : "<span>开始推演 →</span>"}
            </div>
          </div>
        </article>
      </div>
    </section>
  `;
}

function getChapterIllustration(num: number): string {
  return {
    1: "/images/forge/ch1-anvil.png",
    2: "/images/forge/ch2-tank-drone.png",
    3: "/images/forge/ch3-pincer.png",
    4: "/images/forge/ch4-formation.png",
    5: "/images/forge/ch5-victory-insignia.png",
  }[num as 1 | 2 | 3 | 4 | 5] ?? "";
}

/** Level ID → generated PNG cover filename (without .png) */
const LEVEL_COVER_MAP: Record<string, string> = {
  '1.1.1': 'CP1-1', '1.1.2': 'CP1-2', '1.1.3': 'CP1-3',
  '1.2.1': 'CP1-4', '1.2.2': 'CP1-5', '1.2.3': 'CP1-6',
  '2.1.1': 'CP2-1', '2.1.2': 'CP2-2',
  '2.2.1': 'CP2-3', '2.2.2': 'CP2-4',
  '3.1.1': 'CP3-1', '3.1.2': 'CP3-2',
  '3.2.1': 'CP3-3', '3.2.2': 'CP3-4', '3.2.3': 'CP3-5',
  '4.1.1': 'CP4-1', '4.1.2': 'CP4-2', '4.2.1': 'CP4-3',
  '5.1.1': 'CP5-1', '5.1.2': 'CP5-2', '5.2.1': 'CP5-3',
  'exam-1': 'EX-1', 'exam-2': 'EX-2', 'exam-3': 'EX-3', 'exam-4': 'EX-4',
};

function getLevelCover(id: string): string {
  const coverId = LEVEL_COVER_MAP[id] || id;
  return `/images/forge/covers/${coverId}.png`;
}

function renderStory(ch: ChapterData): string {
  if (!ch.story || !ch.story.lines.length) return "";
  return `
    <div class="s12-story-insert">
      <div class="s12-story-label">${ch.story.label}</div>
      <div class="s12-story-body">${ch.story.lines.join("<br><br>")}</div>
      <div class="s12-story-icon">📋</div>
    </div>
  `;
}

// ===== 事件绑定 =====

function bindEvents(): void {
  // 已解锁章节支持整卡点击，卡片内按钮通过 stopPropagation 避免重复触发。
  document.querySelectorAll<HTMLElement>(".s12-card:not(.s12-card--locked)").forEach(card => {
    const openChapter = () => {
      const ch = Number(card.dataset.ch);
      if (ch) selectChapter(ch);
    };
    card.addEventListener("click", (event) => {
      if ((event.target as HTMLElement).closest(".s12-card-action")) return;
      openChapter();
    });
    card.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      openChapter();
    });
  });

  // 章节操作按钮：卡片点击和按钮点击都打开章节详情。
  document.querySelectorAll<HTMLButtonElement>(".s12-card-action").forEach(button => {
    button.addEventListener("click", (event) => {
      event.stopPropagation();
      const ch = Number(button.closest<HTMLElement>(".s12-card")?.dataset.ch);
      if (ch) selectChapter(ch);
    });
  });

  // 锚点点击
  document.querySelectorAll<HTMLElement>(".s12-card-anchor").forEach(anchor => {
    anchor.addEventListener("click", () => {
      const ch = Number(anchor.dataset.ch);
      const data = activeChapters.find(c => c.num === ch);
      if (!data || data.state === "locked") return;
      selectChapter(ch);
      setTimeout(() => {
        const el = document.querySelector(".s12-story-insert");
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 450);
    });
  });

  // 共享 header 事件（返回 + 导航按钮）
  bindHeaderEvents(forgeGo, pathFor("armory-hall"));

  // ESC 关闭详情
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") closeDetail();
  });
}

// ===== 章节选择 =====

let activeChapter: number | null = null;

function selectChapter(ch: number): void {
  const data = activeChapters.find(c => c.num === ch);
  if (!data || data.state === "locked") return;

  // V9: 重玩检查（从屏 13 我的作品集点击重玩按钮跳转过来）
  const replayLevelId = sessionStorage.getItem("battlecode-forge-replay");
  if (replayLevelId) {
    sessionStorage.removeItem("battlecode-forge-replay");
    // 自动选中该关卡
    setTimeout(() => {
      const levelRow = document.querySelector(`.s12-level-card[data-lid="${replayLevelId}"]`) as HTMLElement;
      if (levelRow) {
        levelRow.click();
      }
    }, 100);
  }

  // 重复点击同一章 = 关闭
  if (activeChapter === ch) {
    closeDetail();
    return;
  }

  activeChapter = ch;
  const main = document.getElementById("s12-main")!;

  // 清除旧选中态
  document.querySelectorAll(".s12-card").forEach(c => c.classList.remove("s12-card--selected"));
  const card = document.querySelector(`.s12-card[data-ch="${ch}"]`);
  card?.classList.add("s12-card--selected");

  // 渲染详情
  const panel = document.getElementById("s12-detail-panel")!;
  panel.innerHTML = renderDetail(data);

  // 展开
  main.classList.add("s12-main--expanded");
  requestAnimationFrame(() => {
    panel.classList.add("s12-detail-panel--visible");
  });

  // 绑定关闭 + 关卡行点击
  bindDetailEvents();
}

function closeDetail(): void {
  const main = document.getElementById("s12-main")!;
  const panel = document.getElementById("s12-detail-panel")!;

  panel.classList.remove("s12-detail-panel--visible");
  setTimeout(() => {
    main.classList.remove("s12-main--expanded");
    document.querySelectorAll(".s12-card").forEach(c => c.classList.remove("s12-card--selected"));
    activeChapter = null;
    panel.innerHTML = "";
  }, 250);
}

function resumePendingForge(): void {
  const raw = localStorage.getItem(PENDING_FORGE_KEY);
  if (!raw || !isLoggedIn()) return;
  localStorage.removeItem(PENDING_AUTH_PATH_KEY);

  let pending: { forgeId?: string; chapter?: number };
  try {
    pending = JSON.parse(raw) as { forgeId?: string; chapter?: number };
  } catch {
    localStorage.removeItem(PENDING_FORGE_KEY);
    return;
  }
  if (!pending.forgeId || !pending.chapter) return;

  const chapter = activeChapters.find((item) => item.num === pending.chapter);
  const level = chapter?.sections.flatMap((section) => section.items).find((item) => item.id === pending.forgeId);
  const isExam = !!chapter?.exam && pending.forgeId === `exam-${chapter.num}`;
  if (!chapter || (!level && !isExam)) {
    localStorage.removeItem(PENDING_FORGE_KEY);
    return;
  }

  localStorage.removeItem(PENDING_FORGE_KEY);
  selectChapter(chapter.num);
  window.setTimeout(() => {
    const selector = `.s12-level-card[data-lid="${pending.forgeId}"]`;
    document.querySelector<HTMLElement>(selector)?.click();
  }, 320);
}

function bindDetailEvents(): void {
  // 返回按钮位于章档案上方，保留旧按钮选择器兼容过渡动画。
  document.querySelector("#s12-detail-back")?.addEventListener("click", closeDetail);
  document.querySelector("#s12-master-back")?.addEventListener("click", closeDetail);

  // 关卡小卡片点击 → 先进入 loading page，再进入推演编辑器；锁定卡片不响应。
  let openingLevel = false;

  const openForgeProject = async (forgeId: string, levelName: string, resultSource: string): Promise<void> => {
    if (openingLevel) return;
    openingLevel = true;
    const app = document.getElementById("app");
    if (!app) {
      openingLevel = false;
      return;
    }

    const loading = renderLevelLoadingPage(app, {
      mode: "overlay",
      title: `${forgeId} · ${levelName}`,
      subtitle: "正在建立关卡数据与编辑器工作区",
    });

    try {
      if (!FORGE_LEVEL_MAP[forgeId]) {
        throw new Error(`未找到关卡数据映射: ${forgeId}`);
      }
      if (!isLoggedIn()) {
        localStorage.setItem(PENDING_AUTH_PATH_KEY, pathFor("forge"));
        localStorage.setItem(PENDING_FORGE_KEY, JSON.stringify({
          forgeId,
          chapter: activeChapter,
        }));
        loading.setAuthRequired(
          () => {
            loading.dispose();
            openingLevel = false;
            forgeGo(pathFor("home"));
          },
          () => {
            loading.dispose();
            openingLevel = false;
            localStorage.removeItem(PENDING_AUTH_PATH_KEY);
            localStorage.removeItem(PENDING_FORGE_KEY);
          },
        );
        return;
      }
      await preloadForgeLevel(forgeId, (stage, detail) => loading.setStage(stage, detail));
      if (!loading.isMounted()) {
        openingLevel = false;
        return;
      }

      loading.setStage("editor", "准备 Monaco 编辑器与作战工作区");
      const proj = createFromLevel(`forge-${forgeId}`, levelName, DEFAULT_BOT_CODE);
      localStorage.setItem("battlecode-result-source", resultSource);
      loading.dispose();
      openingLevel = false;
      forgeGo(pathFor("editor", proj.id));
    } catch (error) {
      if (!loading.isMounted()) {
        openingLevel = false;
        return;
      }
      loading.setError(
        error,
        () => {
          loading.dispose();
          openingLevel = false;
          void openForgeProject(forgeId, levelName, resultSource);
        },
        () => {
          loading.dispose();
          openingLevel = false;
        },
      );
    }
  };

  const openLevel = (id: string | undefined): void => {
    const ch = activeChapters.find(c => c.num === activeChapter);
    const level = id ? ch?.sections.flatMap(s => s.items).find(l => l.id === id) : null;
    if (!level || level.state === "locked" || openingLevel) return;
    void openForgeProject(id!, level.name, `forge:ch${activeChapter}:${id}`);
  };
  document.querySelectorAll<HTMLElement>('.s12-level-card[role="button"]').forEach(card => {
    const activate = () => openLevel(card.dataset.lid);
    card.addEventListener("click", activate);
    card.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      activate();
    });
  });
  document.querySelector<HTMLButtonElement>(".s12-objective-action")?.addEventListener("click", () => {
    openLevel(document.querySelector<HTMLButtonElement>(".s12-objective-action")?.dataset.lid);
  });

  // 晋升试炼作为特殊关卡小卡片。
  const examCard = document.querySelector<HTMLElement>('.s12-level-card[data-exam="true"][role="button"]');
  examCard?.addEventListener("click", () => {
    const ch = activeChapters.find(c => c.num === activeChapter);
    if (!ch?.exam || ch.exam.state === "locked" || openingLevel) return;
    const examId = `exam-${ch.num}`;
    void openForgeProject(examId, ch.exam.name, `forge:ch${ch.num}:exam`);
  });
  examCard?.addEventListener("keydown", (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    examCard.click();
  });
}

// ===== 工具函数 =====

function toChineseNum(n: number): string {
  return ["", "一", "二", "三", "四", "五"][n] || String(n);
}

function getChapterDesc(num: number): string {
  const descs: Record<number, string> = {
    1: "第一台推演机体不是实训中心的制式装备。炮管有磨损，编号被刻意抹去。导师在你身后说：'先让它动起来。'他把真正想说的话，留到了你第一次开火之后。",
    2: "战颅第一次沉默了。不是系统故障——是你的战术进入了它没有训练数据的极限空间。无人机解锁，编队规模翻倍。但增援带来的不是安心——是更大的一团迷雾。",
    3: "通信解锁，战场上第一次出现了你没写过的战术。三台 Agent 不是并肩作战——它们在互相转告你听不到的信息。战颅的教学语句越来越少，排名的数字越来越刺眼。你开始察觉：和你竞争的不是 AI，是推演室里坐在隔壁的那个人。",
    4: "第四台 Agent 入列——猫猫车。炮火照亮战场的那一刻，战颅彻底停止了主动建议。它不再说'你应该'——只留下一行：'基于历史数据，你的胜率高于我的建议模型。'你终于看清了锻造链的全貌。但刃成之后——要砍谁？",
    5: "系统简报只剩一行字：你知道该做什么。但你不知道的是——你过去每一场推演的决策、每一次比战颅多想一步的瞬间，都在回答一个冯旸赫没来得及回答的问题。承刃不是终点。是起点。",
  };
  return descs[num] || "";
}
